import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import Select from 'react-select'

const labFieldLabels = {
  labGroupKey: 'หมวดแล็บ (รหัสกลุ่ม)',
  labGroupName: 'หมวดแล็บ (ชื่อหมวด)',
  labProfileName: 'ชุดตรวจ / หมวดย่อย',
}

const ReportGroupPaginationControls = ({ item, dataSourceList = [], onChange }) => {
  const options = useMemo(() => {
    const source = item.dataSourceId === undefined ? undefined : dataSourceList.find(
      (entry) => String(entry.dataSourceId) === String(item.dataSourceId),
    )
    const fields = [
      ...(source?.columns || []),
      ...(source?.fieldVariable?.columns || []),
      ...(item.columns || []),
      ...(item.fieldVariable?.columns || []),
    ]
    return [...new Set(fields.filter((field) => typeof field === 'string' && field.trim()))].map((field) => ({
      value: field,
      label: `${labFieldLabels[field] || item.fieldVariable?.props?.[field]?.title || field} (${field})`,
    }))
  }, [item.dataSourceId, item.columns, item.fieldVariable, dataSourceList])

  const [rowsInput, setRowsInput] = useState(String(item.rowPerPage ?? ''))
  useEffect(() => setRowsInput(String(item.rowPerPage ?? '')), [item.id, item.rowPerPage])

  if (item.type !== 'advance_table' || item.renderUI !== 'GROUP_LEVEL') return null

  const enabled = Boolean(item.pageBreakGroupBy)
  const headingLevels = new Set((item.columns || []).filter((column) =>
    item.fieldVariable?.props?.[column]?.groupDisplay === true,
  ).map((column) => item.fieldVariable.props[column].groupLevel || 'level1'))
  const minimumRows = headingLevels.size + 1
  const validRows = (value) => Number.isInteger(Number(value)) && Number(value) >= minimumRows
  const rowsInvalid = rowsInput !== '' && !validRows(rowsInput)
  const fieldId = `report-page-group-${item.id}`
  const rowsId = `report-page-group-rows-${item.id}`
  const available = options.some((option) => option.value === item.pageBreakGroupBy)
  const selected = options.find((option) => option.value === item.pageBreakGroupBy) || (
    enabled ? { value: item.pageBreakGroupBy, label: `${item.pageBreakGroupBy} (ไม่พบฟิลด์)` } : null
  )
  const update = (values) => {
    const next = { ...item, ...values }
    delete next._groupPages
    onChange(next)
  }
  const toggle = (checked) => {
    const primary = (item.columns || []).find((field) =>
      item.fieldVariable?.props?.[field]?.groupDisplay === true &&
      (item.fieldVariable.props[field].groupLevel || 'level1') === 'level1',
    )
    const preferred = options.find((option) => option.value === 'labGroupKey') ||
      options.find((option) => option.value === primary) || options[0]
    update({
      pageBreakGroupBy: checked ? preferred?.value || '' : '',
      ...(checked && !(parseInt(item.rowPerPage, 10) > 0) ? { rowPerPage: '36' } : {}),
    })
  }

  return (
    <div className="mt-2 mb-2">
      <Form.Check
        id={`${fieldId}-enabled`}
        type="checkbox"
        label="ขึ้นหน้าใหม่เมื่อเปลี่ยนกลุ่ม"
        title="เริ่มหน้าใหม่ตามฟิลด์แบ่งกลุ่มที่เลือก"
        checked={enabled}
        disabled={!options.length && !enabled}
        onChange={(event) => toggle(event.target.checked)}
      />
      {enabled && (
        <Form.Group className="mt-2">
          <Form.Label htmlFor={fieldId}>ฟิลด์แบ่งกลุ่ม</Form.Label>
          <Select
            inputId={fieldId}
            options={options}
            value={selected}
            placeholder="เลือกฟิลด์แบ่งกลุ่ม"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            onChange={(option) => update({ pageBreakGroupBy: option?.value || '' })}
          />
          {!available && <Form.Text className="text-danger">ไม่พบฟิลด์ที่ตั้งไว้ใน datasource กรุณาเลือกฟิลด์ใหม่</Form.Text>}
          <Form.Text className="text-muted">
            เลือกหมวดหลักเพื่อรวมชุดตรวจย่อยไว้ด้วยกัน หรือเลือกชุดตรวจเพื่อแยกแต่ละชุดเป็นหน้าใหม่
          </Form.Text>
        </Form.Group>
      )}
      {!options.length && <Form.Text className="text-muted">เพิ่มฟิลด์ใน datasource ก่อนตั้งค่าการแบ่งกลุ่ม</Form.Text>}
      <Form.Group className="mt-2">
        <Form.Label htmlFor={rowsId}>จำนวนแถวต่อหน้า</Form.Label>
        <Form.Control
          id={rowsId}
          type="number"
          min={minimumRows}
          step="1"
          value={rowsInput}
          isInvalid={rowsInvalid}
          placeholder="36"
          onChange={(event) => {
            setRowsInput(event.target.value)
            if (validRows(event.target.value)) update({ rowPerPage: event.target.value })
          }}
          onBlur={() => {
            if (!validRows(rowsInput)) {
              const fallback = validRows(item.rowPerPage) ? String(item.rowPerPage) : '36'
              setRowsInput(fallback)
              if (!validRows(item.rowPerPage)) update({ rowPerPage: fallback })
            }
          }}
        />
        <Form.Control.Feedback type="invalid">ต้องมีอย่างน้อย {minimumRows} แถว เพื่อให้หัวกลุ่มอยู่กับผลตรวจ</Form.Control.Feedback>
        <Form.Text className="text-muted">นับหัวหมวดและหัวชุดตรวจรวมด้วย หมวดยาวจะพิมพ์หัวซ้ำในหน้าต่อ</Form.Text>
      </Form.Group>
    </div>
  )
}

export default ReportGroupPaginationControls
